import {
  afterNextRender,
  computed,
  DestroyRef,
  Directive,
  effect,
  ElementRef,
  inject,
  input,
  Renderer2,
} from '@angular/core';
import {Tab, Tabs} from 'primeng/tabs';
import {TabStatus, TabStatusEnum} from './tab-status.model';
import {calculateTabClass} from './tab-utils';
import {SaveButtonTrackingService} from '../project-step-save-button/save-button-tracking.service';

interface TabIcon {
  name: string;
  color: string;
}

const STATUS_ICON: Record<TabStatusEnum, TabIcon> = {
  [TabStatusEnum.NOT_STARTED]: {name: 'pi-pencil', color: 'text-gray'},
  [TabStatusEnum.IN_PROGRESS]: {name: 'pi-pencil', color: 'text-blue'},
  [TabStatusEnum.COMPLETED]: {name: 'pi-check', color: 'text-green'},
};

const ACTIVE_BAR_COLOR: Record<TabStatusEnum, string> = {
  [TabStatusEnum.NOT_STARTED]: 'var(--color-gray)',
  [TabStatusEnum.IN_PROGRESS]: 'var(--color-blue)',
  [TabStatusEnum.COMPLETED]: 'var(--color-green)',
};

const ACTIVE_ICON_NAME = 'pi-circle-fill';
const UNSAVED_ICON: TabIcon = {name: 'pi-pencil', color: 'text-orange'};

@Directive({
  selector: 'p-tab[appTabStatus]',
  host: {
    class: 'tab-status',
  },
})
export class TabStatusDirective {
  private readonly tab = inject(Tab);
  private readonly tabs = inject(Tabs);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly renderer = inject(Renderer2);
  private readonly saveButtonTracking = inject(SaveButtonTrackingService);
  private readonly destroyRef = inject(DestroyRef);

  readonly appTabStatus = input<TabStatus | TabStatusEnum | undefined>(undefined);
  readonly appTabStatusUnsaved = input<boolean>(false);

  private readonly iconElement: HTMLElement = this.renderer.createElement('i');

  private readonly unsavedMarkerId = crypto.randomUUID();

  private readonly isUnsaved = computed<boolean>(
    () =>
      this.appTabStatusUnsaved() ||
      this.saveButtonTracking.hasUnsavedChanges(this.tabs, String(this.tab.value())),
  );

  private readonly status = computed<TabStatusEnum | undefined>(() => {
    const status = this.appTabStatus();

    if (status === undefined) {
      return undefined;
    }

    return typeof status === 'string' ? status : calculateTabClass(status);
  });

  private readonly isActive = computed<boolean>(
    () => String(this.tab.value()) === String(this.tabs.value()),
  );

  private readonly icon = computed<TabIcon | undefined>(() => {
    const status = this.status();

    if (this.isUnsaved()) {
      return this.isActive() ? {name: ACTIVE_ICON_NAME, color: UNSAVED_ICON.color} : UNSAVED_ICON;
    }

    if (status === undefined) {
      return undefined;
    }

    const statusIcon = STATUS_ICON[status];

    return this.isActive() ? {name: ACTIVE_ICON_NAME, color: statusIcon.color} : statusIcon;
  });

  private readonly activeBarColor = computed<string | undefined>(() => {
    const status = this.status();

    return status === undefined ? undefined : ACTIVE_BAR_COLOR[status];
  });

  constructor() {
    afterNextRender(() =>
      this.renderer.appendChild(this.elementRef.nativeElement, this.iconElement),
    );

    effect(() => this.trackDeclaredUnsavedChanges());

    effect(() => this.renderStatusClass());

    effect(() => this.renderIcon());

    effect(() => this.paintActiveBar());

    this.destroyRef.onDestroy(() => this.saveButtonTracking.unregisterButton(this.unsavedMarkerId));
  }

  private trackDeclaredUnsavedChanges(): void {
    if (!this.appTabStatusUnsaved()) {
      this.saveButtonTracking.unregisterButton(this.unsavedMarkerId);
      return;
    }

    this.saveButtonTracking.registerButton(this.unsavedMarkerId, {
      element: null,
      enclosingTabs: [{tabs: this.tabs, tabValue: String(this.tab.value())}],
    });
  }

  private renderStatusClass(): void {
    const status = this.status();

    if (status === undefined) {
      return;
    }

    const element: HTMLElement = this.elementRef.nativeElement;

    element.classList.toggle(TabStatusEnum.IN_PROGRESS, status === TabStatusEnum.IN_PROGRESS);
    element.classList.toggle(TabStatusEnum.COMPLETED, status === TabStatusEnum.COMPLETED);
  }

  private renderIcon(): void {
    const icon = this.icon();

    this.renderer.setAttribute(
      this.iconElement,
      'class',
      icon ? `pi ${icon.name} ${icon.color} ml-2 shrink-0` : 'hidden',
    );
  }

  private paintActiveBar(): void {
    const activeBarColor = this.activeBarColor();

    if (!this.isActive() || activeBarColor === undefined) {
      return;
    }

    this.tabs.el.nativeElement.style.setProperty('--p-tabs-active-bar-background', activeBarColor);
  }
}
